// src/services/firebaseInvoiceService.js - Fixed with preview numbering
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  orderBy, 
  query,
  where,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';

// Export Firebase functions and db for use in other components
export { doc, deleteDoc, updateDoc, db };

// ===== MULTI-TENANT CONTEXT =====
let currentCompanyId = null;
let currentCompanyName = null;

// Set company context for operations
export const setCompanyContext = (companyId, companyName = null) => {
  currentCompanyId = companyId;
  currentCompanyName = companyName;
  console.log(`Firebase service context set to company: ${companyId}`);
};

// Get current company context
export const getCompanyContext = () => ({
  companyId: currentCompanyId,
  companyName: currentCompanyName
});

// Add company context to data
const addCompanyContext = (data, companyId = null, companyName = null) => {
  return {
    ...data,
    companyId: companyId || currentCompanyId || 'default',
    companyName: companyName || currentCompanyName || 'Unknown Company',
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date()
  };
};

// ===== PREVIEW NUMBERING SYSTEM =====

// Get the next number WITHOUT incrementing the counter (for preview)
export const getNextInvoiceNumberPreview = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId || 'default';
    
    // Use company-specific counter document
    const counterDocId = `invoiceCounter_${targetCompanyId}`;
    const counterRef = doc(db, 'counters', counterDocId);
    
    try {
      const counterDoc = await getDoc(counterRef);
      
      if (counterDoc.exists()) {
        const currentCount = counterDoc.data().count || 0;
        const nextNumber = currentCount + 1;
        
        const previewNumber = `INV${nextNumber.toString().padStart(4, '0')}`;
        console.log(`Generated preview invoice number: ${previewNumber} for company: ${targetCompanyId}`);
        return previewNumber;
      } else {
        // Counter doesn't exist yet, preview would be INV0001
        const previewNumber = 'INV0001';
        console.log(`Generated preview invoice number (new counter): ${previewNumber} for company: ${targetCompanyId}`);
        return previewNumber;
      }
    } catch (error) {
      console.error('Error getting preview number:', error);
      // Fallback: scan existing invoices for this company to find what the next number would be
      const invoicesQuery = query(
        collection(db, 'invoices'), 
        where('companyId', '==', targetCompanyId)
      );
      const invoicesSnapshot = await getDocs(invoicesQuery);
      
      let highestNumber = 0;
      invoicesSnapshot.forEach((doc) => {
        const invoice = doc.data();
        if (invoice.invoiceNumber) {
          let numberPart;
          
          // Handle both regular (INV0001) and overdue (INV0002-EXT) formats
          if (invoice.invoiceNumber.includes('-EXT')) {
            numberPart = invoice.invoiceNumber.replace('INV', '').replace('-EXT', '');
          } else if (invoice.invoiceNumber.startsWith('INV')) {
            numberPart = invoice.invoiceNumber.replace('INV', '');
          }
          
          if (numberPart) {
            const num = parseInt(numberPart, 10);
            if (!isNaN(num) && num > highestNumber) {
              highestNumber = num;
            }
          }
        }
      });
      
      const nextNumber = highestNumber + 1;
      const paddedNumber = nextNumber.toString().padStart(4, '0');
      return `INV${paddedNumber}`;
    }
  } catch (error) {
    console.error('Error generating preview invoice number:', error);
    // Ultimate fallback to a placeholder
    return 'INV????';
  }
};

// Get preview for overdue invoice number
export const getNextOverdueInvoiceNumberPreview = async (companyId = null) => {
  try {
    console.log('Generating overdue preview invoice number...');
    
    // Get the preview base number (without incrementing)
    const basePreviewNumber = await getNextInvoiceNumberPreview(companyId);
    
    // Add -EXT suffix for overdue preview
    const overduePreviewNumber = `${basePreviewNumber}-EXT`;
    
    console.log(`Generated overdue preview invoice number: ${overduePreviewNumber} for company: ${companyId || currentCompanyId || 'default'}`);
    return overduePreviewNumber;
  } catch (error) {
    console.error('Error generating overdue preview invoice number:', error);
    return 'INV????-EXT';
  }
};

// ===== ACTUAL INVOICE NUMBERING (ONLY WHEN CREATING) =====

// Generate actual invoice number - unified sequence for both regular and overdue with company isolation
// This ACTUALLY increments the counter and should only be called when creating an invoice
export const generateInvoiceNumber = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId || 'default';
    
    console.log(`🔢 Generating ACTUAL invoice number for company: ${targetCompanyId}`);
    
    // Use company-specific counter document (unified for both regular and overdue)
    const counterDocId = `invoiceCounter_${targetCompanyId}`;
    const counterRef = doc(db, 'counters', counterDocId);
    
    try {
      const counterDoc = await getDoc(counterRef);
      
      if (counterDoc.exists()) {
        const currentCount = counterDoc.data().count || 0;
        const newCount = currentCount + 1;
        
        await updateDoc(counterRef, { 
          count: newCount,
          lastUpdated: new Date(),
          companyId: targetCompanyId,
          lastGeneratedBy: 'generateInvoiceNumber'
        });
        
        const invoiceNumber = `INV${newCount.toString().padStart(4, '0')}`;
        console.log(`✅ Generated ACTUAL invoice number: ${invoiceNumber} for company: ${targetCompanyId}`);
        return invoiceNumber;
      } else {
        // Initialize counter for this company
        await setDoc(counterRef, { 
          count: 1,
          createdAt: new Date(),
          lastUpdated: new Date(),
          companyId: targetCompanyId,
          lastGeneratedBy: 'generateInvoiceNumber'
        });
        
        const invoiceNumber = 'INV0001';
        console.log(`✅ Initialized invoice counter with ACTUAL: ${invoiceNumber} for company: ${targetCompanyId}`);
        return invoiceNumber;
      }
    } catch (error) {
      console.error('Error with counter document:', error);
      // Fallback: scan existing invoices for this company to find highest number
      const invoicesQuery = query(
        collection(db, 'invoices'), 
        where('companyId', '==', targetCompanyId)
      );
      const invoicesSnapshot = await getDocs(invoicesQuery);
      
      let highestNumber = 0;
      invoicesSnapshot.forEach((doc) => {
        const invoice = doc.data();
        if (invoice.invoiceNumber) {
          let numberPart;
          
          // Handle both regular (INV0001) and overdue (INV0002-EXT) formats
          if (invoice.invoiceNumber.includes('-EXT')) {
            numberPart = invoice.invoiceNumber.replace('INV', '').replace('-EXT', '');
          } else if (invoice.invoiceNumber.startsWith('INV')) {
            numberPart = invoice.invoiceNumber.replace('INV', '');
          }
          
          if (numberPart) {
            const num = parseInt(numberPart, 10);
            if (!isNaN(num) && num > highestNumber) {
              highestNumber = num;
            }
          }
        }
      });
      
      const nextNumber = highestNumber + 1;
      
      // Initialize the counter with the correct number
      try {
        await setDoc(counterRef, { 
          count: nextNumber,
          createdAt: new Date(),
          lastUpdated: new Date(),
          companyId: targetCompanyId,
          lastGeneratedBy: 'generateInvoiceNumber_fallback'
        });
      } catch (setError) {
        console.error('Error initializing counter in fallback:', setError);
      }
      
      const paddedNumber = nextNumber.toString().padStart(4, '0');
      return `INV${paddedNumber}`;
    }
  } catch (error) {
    console.error('Error generating invoice number:', error);
    // Ultimate fallback to timestamp-based number
    const timestamp = Date.now().toString().slice(-4);
    return `INV${timestamp}`;
  }
};

// Generate overdue invoice number using unified counter with -EXT suffix
// This ACTUALLY increments the counter and should only be called when creating an invoice
export const generateOverdueInvoiceNumber = async (companyId = null) => {
  try {
    console.log('🔢 Generating ACTUAL overdue invoice number using unified counter...');
    
    // Use the same unified counter as regular invoices
    const baseInvoiceNumber = await generateInvoiceNumber(companyId);
    
    // Add -EXT suffix for overdue invoices
    const overdueInvoiceNumber = `${baseInvoiceNumber}-EXT`;
    
    console.log(`✅ Generated ACTUAL unified overdue invoice number: ${overdueInvoiceNumber} for company: ${companyId || currentCompanyId || 'default'}`);
    return overdueInvoiceNumber;
  } catch (error) {
    console.error('Error generating overdue invoice number:', error);
    // Fallback to regular invoice number with -EXT suffix
    const timestamp = Date.now().toString().slice(-4);
    return `INV${timestamp}-EXT`;
  }
};

// ===== INVOICE MANAGEMENT WITH MULTI-TENANT SUPPORT =====

// Add a new invoice with company context
export const addInvoice = async (invoiceData, companyId = null, companyName = null) => {
  try {
    const invoiceWithContext = addCompanyContext(invoiceData, companyId, companyName);
    
    console.log('Adding invoice to Firebase with company context:', {
      invoiceNumber: invoiceWithContext.invoiceNumber,
      companyId: invoiceWithContext.companyId,
      companyName: invoiceWithContext.companyName
    });
    
    const docRef = await addDoc(collection(db, 'invoices'), invoiceWithContext);
    
    console.log('Invoice added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding invoice:', error);
    throw error;
  }
};

// Get invoices with company filtering
export const getInvoices = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const invoicesCollection = collection(db, 'invoices');
    
    let q;
    if (targetCompanyId) {
      try {
        // Try with company filter and ordering
        q = query(
          invoicesCollection, 
          where('companyId', '==', targetCompanyId),
          orderBy('createdAt', 'desc')
        );
      } catch (error) {
        // Fallback without orderBy if it fails
        q = query(invoicesCollection, where('companyId', '==', targetCompanyId));
      }
    } else {
      // Fallback: get all invoices
      try {
        q = query(invoicesCollection, orderBy('createdAt', 'desc'));
      } catch (error) {
        q = invoicesCollection;
      }
    }
    
    const querySnapshot = await getDocs(q);
    const invoices = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Add companyId if missing (backward compatibility)
      if (!data.companyId && targetCompanyId) {
        data.companyId = targetCompanyId;
        data.companyName = data.companyName || currentCompanyName || 'Unknown Company';
      }
      
      // Only include invoices for the specified company
      if (!targetCompanyId || data.companyId === targetCompanyId) {
        invoices.push({ id: doc.id, ...data });
      }
    });
    
    // Sort manually if orderBy wasn't used
    invoices.sort((a, b) => {
      const dateA = a.createdAt ? 
        (a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt)) : 
        new Date();
      const dateB = b.createdAt ? 
        (b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt)) : 
        new Date();
      return dateB - dateA;
    });
    
    console.log(`Retrieved ${invoices.length} invoices${targetCompanyId ? ` for company ${targetCompanyId}` : ''}`);
    return invoices;
  } catch (error) {
    console.error('Error getting invoices:', error);
    throw error;
  }
};

// Subscribe to real-time invoice updates with company filtering
export const subscribeToInvoices = (callback, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const invoicesCollection = collection(db, 'invoices');
    
    let q;
    if (targetCompanyId) {
      q = query(invoicesCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = invoicesCollection;
    }
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const invoices = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Add companyId if missing
          if (!data.companyId && targetCompanyId) {
            data.companyId = targetCompanyId;
            data.companyName = data.companyName || currentCompanyName || 'Unknown Company';
          }
          
          // Only include invoices for the specified company
          if (!targetCompanyId || data.companyId === targetCompanyId) {
            invoices.push({ id: doc.id, ...data });
          }
        });
        
        // Sort by creation date
        invoices.sort((a, b) => {
          const dateA = a.createdAt ? 
            (a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt)) : 
            new Date();
          const dateB = b.createdAt ? 
            (b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt)) : 
            new Date();
          return dateB - dateA;
        });
        
        console.log(`Real-time invoice update: ${invoices.length} invoices${targetCompanyId ? ` for company ${targetCompanyId}` : ''}`);
        callback(invoices);
      },
      (error) => {
        console.error('Error in invoice subscription:', error);
        callback([]);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up invoice subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

// Update an invoice
export const updateInvoice = async (invoiceId, updateData) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    await updateDoc(invoiceRef, {
      ...updateData,
      updatedAt: new Date()
    });
    
    console.log('Invoice updated:', invoiceId);
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

// Delete an invoice
export const deleteInvoice = async (invoiceId) => {
  try {
    await deleteDoc(doc(db, 'invoices', invoiceId));
    console.log('Invoice deleted:', invoiceId);
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

// ===== RENTAL MANAGEMENT WITH MULTI-TENANT SUPPORT =====

// Add a new rental with company context
export const addRental = async (rentalData, companyId = null, companyName = null) => {
  try {
    const rentalWithContext = addCompanyContext(rentalData, companyId, companyName);
    
    const docRef = await addDoc(collection(db, 'motorbike-rentals'), rentalWithContext);
    
    console.log('Rental added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding rental:', error);
    throw error;
  }
};

// Get rentals with company filtering
export const getRentals = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const rentalsCollection = collection(db, 'motorbike-rentals');
    
    let q;
    if (targetCompanyId) {
      q = query(rentalsCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = rentalsCollection;
    }
    
    const querySnapshot = await getDocs(q);
    const rentals = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Add companyId if missing
      if (!data.companyId && targetCompanyId) {
        data.companyId = targetCompanyId;
        data.companyName = data.companyName || currentCompanyName || 'Unknown Company';
      }
      
      // Only include rentals for the specified company
      if (!targetCompanyId || data.companyId === targetCompanyId) {
        rentals.push({ id: doc.id, ...data });
      }
    });
    
    console.log(`Retrieved ${rentals.length} rentals${targetCompanyId ? ` for company ${targetCompanyId}` : ''}`);
    return rentals;
  } catch (error) {
    console.error('Error getting rentals:', error);
    throw error;
  }
};

// Subscribe to real-time rental updates with company filtering
export const subscribeToRentals = (callback, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const rentalsCollection = collection(db, 'motorbike-rentals');
    
    let q;
    if (targetCompanyId) {
      q = query(rentalsCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = rentalsCollection;
    }
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const rentals = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Add companyId if missing
          if (!data.companyId && targetCompanyId) {
            data.companyId = targetCompanyId;
            data.companyName = data.companyName || currentCompanyName || 'Unknown Company';
          }
          
          // Only include rentals for the specified company
          if (!targetCompanyId || data.companyId === targetCompanyId) {
            rentals.push({ id: doc.id, ...data });
          }
        });
        
        callback(rentals);
      },
      (error) => {
        console.error('Error in rental subscription:', error);
        callback([]);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up rental subscription:', error);
    return () => {};
  }
};

// Update a rental
export const updateRental = async (rentalId, updateData) => {
  try {
    const rentalRef = doc(db, 'motorbike-rentals', rentalId);
    await updateDoc(rentalRef, {
      ...updateData,
      updatedAt: new Date()
    });
    
    console.log('Rental updated:', rentalId);
  } catch (error) {
    console.error('Error updating rental:', error);
    throw error;
  }
};

// Delete a rental
export const deleteRental = async (rentalId) => {
  try {
    await deleteDoc(doc(db, 'motorbike-rentals', rentalId));
    console.log('Rental deleted:', rentalId);
  } catch (error) {
    console.error('Error deleting rental:', error);
    throw error;
  }
};

// ===== RESERVATION MANAGEMENT WITH MULTI-TENANT SUPPORT =====

// Add a new reservation with company context
export const addReservation = async (reservationData, companyId = null, companyName = null) => {
  try {
    const reservationWithContext = addCompanyContext(reservationData, companyId, companyName);
    
    const docRef = await addDoc(collection(db, 'motorbike-reservations'), reservationWithContext);
    
    console.log('Reservation added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding reservation:', error);
    throw error;
  }
};

// Get reservations with company filtering
export const getReservations = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const reservationsCollection = collection(db, 'motorbike-reservations');
    
    let q;
    if (targetCompanyId) {
      q = query(reservationsCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = reservationsCollection;
    }
    
    const querySnapshot = await getDocs(q);
    const reservations = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Add companyId if missing
      if (!data.companyId && targetCompanyId) {
        data.companyId = targetCompanyId;
        data.companyName = data.companyName || currentCompanyName || 'Unknown Company';
      }
      
      // Only include reservations for the specified company
      if (!targetCompanyId || data.companyId === targetCompanyId) {
        reservations.push({ id: doc.id, ...data });
      }
    });
    
    console.log(`Retrieved ${reservations.length} reservations${targetCompanyId ? ` for company ${targetCompanyId}` : ''}`);
    return reservations;
  } catch (error) {
    console.error('Error getting reservations:', error);
    throw error;
  }
};

// Update a reservation
export const updateReservation = async (reservationId, updateData) => {
  try {
    const reservationRef = doc(db, 'motorbike-reservations', reservationId);
    await updateDoc(reservationRef, {
      ...updateData,
      updatedAt: new Date()
    });
    
    console.log('Reservation updated:', reservationId);
  } catch (error) {
    console.error('Error updating reservation:', error);
    throw error;
  }
};

// Subscribe to reservations with company filtering
export const subscribeToReservations = (callback, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const reservationsCollection = collection(db, 'motorbike-reservations');
    
    let q;
    if (targetCompanyId) {
      q = query(reservationsCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = reservationsCollection;
    }
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const reservations = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Add companyId if missing
          if (!data.companyId && targetCompanyId) {
            data.companyId = targetCompanyId;
            data.companyName = data.companyName || currentCompanyName || 'Unknown Company';
          }
          
          // Only include reservations for the specified company
          if (!targetCompanyId || data.companyId === targetCompanyId) {
            reservations.push({ id: doc.id, ...data });
          }
        });
        
        callback(reservations);
      },
      (error) => {
        console.error('Error in reservations subscription:', error);
        callback([]);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up reservations subscription:', error);
    return () => {};
  }
};

// ===== INCOME MANAGEMENT WITH MULTI-TENANT SUPPORT =====

// Add multiple income entries for a bike with company context
export const addMultipleIncomeEntries = async (bikeId, incomeEntries, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId || 'default';
    
    console.log(`Adding ${incomeEntries.length} income entries for bike: ${bikeId} (company: ${targetCompanyId})`);
    
    // Create company-specific bike income document ID
    const bikeIncomeDocId = `${targetCompanyId}_${bikeId}`;
    const bikeIncomeRef = doc(db, 'bike-income', bikeIncomeDocId);
    const bikeIncomeSnap = await getDoc(bikeIncomeRef);
    
    let currentIncomeData = { entries: [] };
    if (bikeIncomeSnap.exists()) {
      currentIncomeData = bikeIncomeSnap.data();
    }
    
    // Add company context to each income entry
    const entriesWithContext = incomeEntries.map(entry => ({
      ...entry,
      companyId: targetCompanyId,
      companyName: entry.companyName || currentCompanyName || 'Unknown Company',
      createdAt: entry.createdAt || new Date()
    }));
    
    // Add new entries
    const updatedEntries = [...(currentIncomeData.entries || []), ...entriesWithContext];
    
    // Calculate total income
    const totalIncome = updatedEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
    
    // Update document with company context
    await setDoc(bikeIncomeRef, {
      bikeId: bikeId,
      companyId: targetCompanyId,
      companyName: currentCompanyName || 'Unknown Company',
      entries: updatedEntries,
      totalIncome: totalIncome,
      lastUpdated: new Date(),
      entryCount: updatedEntries.length
    });
    
    console.log(`✅ Successfully added ${incomeEntries.length} income entries for ${bikeId} (company: ${targetCompanyId})`);
    
    return true;
  } catch (error) {
    console.error('Error adding multiple income entries:', error);
    throw error;
  }
};

// Add a single income entry
export const addIncomeEntry = async (bikeId, incomeEntry, companyId = null) => {
  return await addMultipleIncomeEntries(bikeId, [incomeEntry], companyId);
};

// Get income data for a specific bike with company context
export const getBikeIncome = async (bikeId, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId || 'default';
    const bikeIncomeDocId = `${targetCompanyId}_${bikeId}`;
    const bikeIncomeRef = doc(db, 'bike-income', bikeIncomeDocId);
    const bikeIncomeSnap = await getDoc(bikeIncomeRef);
    
    if (bikeIncomeSnap.exists()) {
      return bikeIncomeSnap.data();
    } else {
      return { 
        bikeId, 
        companyId: targetCompanyId,
        companyName: currentCompanyName || 'Unknown Company',
        entries: [], 
        totalIncome: 0, 
        entryCount: 0 
      };
    }
  } catch (error) {
    console.error('Error getting bike income:', error);
    throw error;
  }
};

// Get all bike income data for company
export const getAllBikeIncome = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const incomeCollection = collection(db, 'bike-income');
    
    let q;
    if (targetCompanyId) {
      q = query(incomeCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = incomeCollection;
    }
    
    const querySnapshot = await getDocs(q);
    const bikeIncomeData = {};
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Only include income for the specified company
      if (!targetCompanyId || data.companyId === targetCompanyId) {
        bikeIncomeData[data.bikeId] = data;
      }
    });
    
    console.log(`Retrieved income data for ${Object.keys(bikeIncomeData).length} bikes${targetCompanyId ? ` (company: ${targetCompanyId})` : ''}`);
    return bikeIncomeData;
  } catch (error) {
    console.error('Error getting all bike income:', error);
    throw error;
  }
};

// Subscribe to real-time income updates for a specific bike
export const subscribeToBikeIncome = (bikeId, callback, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId || 'default';
    const bikeIncomeDocId = `${targetCompanyId}_${bikeId}`;
    const bikeIncomeRef = doc(db, 'bike-income', bikeIncomeDocId);
    
    const unsubscribe = onSnapshot(
      bikeIncomeRef,
      (doc) => {
        if (doc.exists()) {
          callback(doc.data());
        } else {
          callback({ 
            bikeId, 
            companyId: targetCompanyId,
            companyName: currentCompanyName || 'Unknown Company',
            entries: [], 
            totalIncome: 0, 
            entryCount: 0 
          });
        }
      },
      (error) => {
        console.error('Error in bike income subscription:', error);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up bike income subscription:', error);
    return () => {};
  }
};

// Subscribe to all bike income updates for company
export const subscribeToAllBikeIncome = (callback, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const incomeCollection = collection(db, 'bike-income');
    
    let q;
    if (targetCompanyId) {
      q = query(incomeCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = incomeCollection;
    }
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const bikeIncomeData = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Only include income for the specified company
          if (!targetCompanyId || data.companyId === targetCompanyId) {
            bikeIncomeData[data.bikeId] = data;
          }
        });
        callback(bikeIncomeData);
      },
      (error) => {
        console.error('Error in all bike income subscription:', error);
        callback({});
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up all bike income subscription:', error);
    return () => {};
  }
};

// ===== UTILITY FUNCTIONS WITH MULTI-TENANT SUPPORT =====

// Get invoice by invoice number with company context
export const getInvoiceByNumber = async (invoiceNumber, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const invoicesCollection = collection(db, 'invoices');
    
    let q;
    if (targetCompanyId) {
      q = query(
        invoicesCollection, 
        where('invoiceNumber', '==', invoiceNumber),
        where('companyId', '==', targetCompanyId)
      );
    } else {
      q = query(invoicesCollection, where('invoiceNumber', '==', invoiceNumber));
    }
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting invoice by number:', error);
    throw error;
  }
};

// Get rentals by bike with company context
export const getRentalsByBike = async (bikeId, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const rentalsCollection = collection(db, 'motorbike-rentals');
    
    let q;
    if (targetCompanyId) {
      q = query(
        rentalsCollection, 
        where('bike', '==', bikeId),
        where('companyId', '==', targetCompanyId)
      );
    } else {
      q = query(rentalsCollection, where('bike', '==', bikeId));
    }
    
    const querySnapshot = await getDocs(q);
    const rentals = [];
    
    querySnapshot.forEach((doc) => {
      rentals.push({ id: doc.id, ...doc.data() });
    });
    
    return rentals;
  } catch (error) {
    console.error('Error getting rentals by bike:', error);
    throw error;
  }
};

// ===== TODAY'S PICKUPS UTILITY FUNCTIONS =====

// Check if a date is today
export const isToday = (dateString) => {
  if (!dateString) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const checkDate = new Date(dateString);
  checkDate.setHours(0, 0, 0, 0);
  
  return today.getTime() === checkDate.getTime();
};

// Get today's pickups from rentals array
export const getTodaysPickups = (rentals) => {
  if (!Array.isArray(rentals)) return [];
  
  return rentals.filter(rental => {
    if (!rental.startDate) return false;
    if (!isToday(rental.startDate)) return false;
    if (rental.status === 'Returned') return false;
    
    return true;
  });
};

// Calculate rental duration in days
export const calculateRentalDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

// Get model rate for pricing
export const getModelRate = (model) => {
  const rates = {
    'DIO': 25,
    'APRILIA': 30,
    'XPULSE': 35,
    'XBLADE': 30,
    'HORNET': 35,
    'SHINE': 25,
    'NAVI': 20,
    'TRIGGER': 25,
    'NTORQ': 25,
    'HONDA CD': 25,
    'XR': 30,
    'DEFAULT': 25
  };
  
  return rates[model] || rates['DEFAULT'];
};

// Calculate overdue cost
export const calculateOverdueCost = (bikeModel, daysOverdue) => {
  if (!bikeModel || daysOverdue <= 0) return 0;
  
  let model = bikeModel;
  if (bikeModel.includes(' - ')) {
    model = bikeModel.split(' - ')[0];
  }
  
  const dailyRate = getModelRate(model);
  return dailyRate * daysOverdue;
};

// Check overdue status
export const checkOverdueStatus = (rental) => {
  if (!rental || !rental.endDate) {
    return { isOverdue: false, daysOverdue: 0 };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const endDate = new Date(rental.endDate);
  endDate.setHours(0, 0, 0, 0);
  
  if (today > endDate) {
    const diffTime = today - endDate;
    const daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { isOverdue: true, daysOverdue };
  }
  
  return { isOverdue: false, daysOverdue: 0 };
};

// ===== BIKE AVAILABILITY FUNCTIONS =====

// Check if bike is available for given dates using Firebase data
export const isBikeAvailable = async (bikeId, startDate, endDate, companyId = null) => {
  try {
    const rentals = await getRentalsByBike(bikeId, companyId);
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (const rental of rentals) {
      if (rental.status === 'Returned') continue;
      
      const rentalStart = new Date(rental.startDate);
      const rentalEnd = new Date(rental.endDate);
      
      if (start <= rentalEnd && end >= rentalStart) {
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking bike availability:', error);
    return false;
  }
};

// Filter available bikes from a list
export const filterAvailableBikes = async (bikes, startDate, endDate, companyId = null) => {
  try {
    if (!startDate || !endDate || !Array.isArray(bikes)) {
      return bikes || [];
    }

    const availabilityChecks = bikes.map(async (bike) => {
      const isAvailable = await isBikeAvailable(bike.name, startDate, endDate, companyId);
      return isAvailable ? bike : null;
    });

    const results = await Promise.all(availabilityChecks);
    return results.filter(bike => bike !== null);
  } catch (error) {
    console.error('Error filtering available bikes:', error);
    return bikes || [];
  }
};

// Get bike rental status
export const getBikeRentalStatus = async (bikeId, companyId = null) => {
  try {
    const rentals = await getRentalsByBike(bikeId, companyId);
    
    const activeRentals = rentals.filter(rental => rental.status !== 'Returned');
    
    if (activeRentals.length === 0) {
      return { status: 'Available', rental: null };
    }

    activeRentals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const currentRental = activeRentals[0];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(currentRental.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(currentRental.endDate);
    endDate.setHours(0, 0, 0, 0);

    let calculatedStatus = currentRental.status;

    if (today < startDate) {
      calculatedStatus = "Reserved";
    } else if (today.getTime() === startDate.getTime()) {
      calculatedStatus = currentRental.status === "Active" ? "Active" : "Reserved";
    } else if (today > startDate && today < endDate) {
      calculatedStatus = "Active";
    } else if (today.getTime() === endDate.getTime()) {
      calculatedStatus = "Due Today";
    } else if (today > endDate) {
      calculatedStatus = "Overdue";
    }

    return {
      status: calculatedStatus,
      rental: currentRental,
      customerName: currentRental.customerDetails?.name || 'Unknown',
      startDate: currentRental.startDate,
      endDate: currentRental.endDate,
      invoiceNumber: currentRental.invoiceNumber
    };
  } catch (error) {
    console.error('Error getting bike rental status:', error);
    return { status: 'Unknown', rental: null, error: error.message };
  }
};

// ===== ADDITIONAL FUNCTIONS =====

// Update rental statuses
export const updateRentalStatuses = async (companyId = null) => {
  try {
    const rentals = await getRentals(companyId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const updates = [];
    
    for (const rental of rentals) {
      if (!rental.startDate || !rental.endDate || rental.status === 'Returned') {
        continue;
      }
      
      const startDate = new Date(rental.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(rental.endDate);
      endDate.setHours(0, 0, 0, 0);
      
      let newStatus = rental.status;
      
      if (today < startDate) {
        newStatus = "Reserved";
      } else if (today.getTime() === startDate.getTime()) {
        newStatus = rental.status === "Active" ? "Active" : "Reserved";
      } else if (today > startDate && today < endDate) {
        newStatus = "Active";
      } else if (today.getTime() === endDate.getTime()) {
        newStatus = "Due Today";
      } else if (today > endDate) {
        newStatus = "Overdue";
      }
      
      if (newStatus !== rental.status) {
        updates.push(updateRental(rental.id, { status: newStatus }));
      }
    }
    
    await Promise.all(updates);
    console.log(`Updated ${updates.length} rental statuses`);
    
    return updates.length;
  } catch (error) {
    console.error('Error updating rental statuses:', error);
    throw error;
  }
};

// Get all income data (alias for getAllBikeIncome)
export const getAllIncomeData = async (companyId = null) => {
  return await getAllBikeIncome(companyId);
};

// Check bike availability (alias for isBikeAvailable)
export const checkBikeAvailability = async (bikeId, startDate, endDate, companyId = null) => {
  return await isBikeAvailable(bikeId, startDate, endDate, companyId);
};

// Get dashboard stats with company context
export const getDashboardStats = async (companyId = null) => {
  try {
    const [invoices, rentals] = await Promise.all([
      getInvoices(companyId),
      getRentals(companyId)
    ]);
    
    const activeRentals = rentals.filter(r => r.status !== 'Returned').length;
    const totalInvoices = invoices.length;
    const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.totalCost || 0), 0);
    
    return {
      activeRentals,
      totalInvoices,
      totalRevenue,
      recentInvoices: invoices.slice(0, 5)
    };
  } catch (error) {
    console.error('Error getting dashboard stats:', error);
    throw error;
  }
};

// ===== DOCUMENT FIELDS FUNCTIONS =====

export const getDocumentFields = async (companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const fieldsCollection = collection(db, 'document-fields');
    
    let q;
    if (targetCompanyId) {
      q = query(fieldsCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = fieldsCollection;
    }
    
    const querySnapshot = await getDocs(q);
    const fields = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (!targetCompanyId || data.companyId === targetCompanyId) {
        fields.push({ id: doc.id, ...data });
      }
    });
    
    return fields;
  } catch (error) {
    console.error('Error getting document fields:', error);
    throw error;
  }
};

export const subscribeToDocumentFields = (callback, companyId = null) => {
  try {
    const targetCompanyId = companyId || currentCompanyId;
    const fieldsCollection = collection(db, 'document-fields');
    
    let q;
    if (targetCompanyId) {
      q = query(fieldsCollection, where('companyId', '==', targetCompanyId));
    } else {
      q = fieldsCollection;
    }
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fields = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (!targetCompanyId || data.companyId === targetCompanyId) {
            fields.push({ id: doc.id, ...data });
          }
        });
        callback(fields);
      },
      (error) => {
        console.error('Error in document fields subscription:', error);
        callback([]);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up document fields subscription:', error);
    return () => {};
  }
};

export const saveDocumentFields = async (fieldsData, companyId = null, companyName = null) => {
  try {
    const fieldsWithContext = addCompanyContext(fieldsData, companyId, companyName);
    
    const docRef = await addDoc(collection(db, 'document-fields'), fieldsWithContext);
    
    console.log('Document fields saved with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving document fields:', error);
    throw error;
  }
};

// ===== MIGRATION FUNCTIONS =====

// Migrate existing data to include company context
export const migrateDataToCompanyContext = async (defaultCompanyId, defaultCompanyName) => {
  try {
    console.log('Starting data migration to add company context...');
    
    const collections = [
      { name: 'invoices', displayName: 'invoices' },
      { name: 'motorbike-rentals', displayName: 'rentals' },
      { name: 'motorbike-reservations', displayName: 'reservations' },
      { name: 'bike-income', displayName: 'income records' }
    ];
    
    let totalMigrated = 0;
    
    for (const collectionInfo of collections) {
      try {
        const collectionRef = collection(db, collectionInfo.name);
        const querySnapshot = await getDocs(collectionRef);
        
        let migratedCount = 0;
        const updatePromises = [];
        
        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          
          // Only update documents that don't have companyId
          if (!data.companyId) {
            const updatePromise = updateDoc(docSnapshot.ref, {
              companyId: defaultCompanyId,
              companyName: defaultCompanyName,
              migratedAt: new Date(),
              migratedFrom: 'legacy'
            });
            updatePromises.push(updatePromise);
            migratedCount++;
          }
        });
        
        await Promise.all(updatePromises);
        totalMigrated += migratedCount;
        
        console.log(`Migrated ${migratedCount} ${collectionInfo.displayName} to company context`);
      } catch (error) {
        console.error(`Error migrating ${collectionInfo.displayName}:`, error);
      }
    }
    
    console.log(`Successfully migrated ${totalMigrated} total documents to company context`);
    return totalMigrated;
  } catch (error) {
    console.error('Error migrating data:', error);
    throw error;
  }
};

// ===== UNIFIED NUMBERING TEST UTILITY =====

// Test function to demonstrate unified numbering
export const testUnifiedNumbering = async (companyId = null) => {
  try {
    console.log('🧪 Testing unified invoice numbering...');
    
    const regular1 = await generateInvoiceNumber(companyId);
    console.log(`Regular invoice 1: ${regular1}`);
    
    const overdue1 = await generateOverdueInvoiceNumber(companyId);
    console.log(`Overdue invoice 1: ${overdue1}`);
    
    const regular2 = await generateInvoiceNumber(companyId);
    console.log(`Regular invoice 2: ${regular2}`);
    
    const overdue2 = await generateOverdueInvoiceNumber(companyId);
    console.log(`Overdue invoice 2: ${overdue2}`);
    
    console.log('✅ Unified numbering test completed');
    
    return {
      regular1,
      overdue1,
      regular2,
      overdue2
    };
  } catch (error) {
    console.error('Error testing unified numbering:', error);
    throw error;
  }
};


// ===== EXPORT DEFAULT OBJECT =====

export default {
  // Company context functions
  setCompanyContext,
  getCompanyContext,
  
  // Preview numbering functions (NEW - for display without incrementing)
  getNextInvoiceNumberPreview,
  getNextOverdueInvoiceNumberPreview,
  
  // Actual numbering functions (for creating invoices)
  generateInvoiceNumber,
  generateOverdueInvoiceNumber,
  
  // Invoice functions
  addInvoice,
  getInvoices,
  subscribeToInvoices,
  updateInvoice,
  deleteInvoice,
  getInvoiceByNumber,
  
  // Rental functions
  addRental,
  getRentals,
  subscribeToRentals,
  updateRental,
  deleteRental,
  getRentalsByBike,
  checkBikeAvailability,
  updateRentalStatuses,
  
  // Bike availability functions
  isBikeAvailable,
  filterAvailableBikes,
  getBikeRentalStatus,
  
  // Today's pickups utility functions
  isToday,
  getTodaysPickups,
  calculateRentalDuration,
  getModelRate,
  calculateOverdueCost,
  checkOverdueStatus,
  
  // Reservation functions
  addReservation,
  getReservations,
  subscribeToReservations,
  updateReservation,
  
  // Income functions
  addIncomeEntry,
  addMultipleIncomeEntries,
  getBikeIncome,
  getAllBikeIncome,
  getAllIncomeData,
  subscribeToBikeIncome,
  subscribeToAllBikeIncome,
  
  // Document fields functions
  getDocumentFields,
  subscribeToDocumentFields,
  saveDocumentFields,
  
  // Utility functions
  getDashboardStats,
  
  // Migration functions
  migrateDataToCompanyContext,
  
  // Test functions
  testUnifiedNumbering
};

